import { paymentAPI } from "..";
import { getErrorStatus } from "../utils/userFacingError";
import { newIdempotencyKey } from "./pixAutomatico";

/**
 * Client for the Saldo de Negociações routes of enterprise-payments-api: the
 * prepaid, per-company balance that Hall auction bids reserve and the
 * apuração consumes. Radio and TV stay on the weekly Pix Automático charge
 * and never read this balance.
 *
 * Every route is private (company JWT in X-JWT) and mounted under the
 * Payments base (`REACT_APP_API_PAYMENT_URL`, `/payment` behind the Ingress):
 *
 *   GET  /payment/negotiation-balance                     org_payments view | bid view
 *   GET  /payment/negotiation-balance/entries             org_payments view
 *   POST /payment/negotiation-balance/terms-acceptance    org_payments create (Idempotency-Key)
 *   POST /payment/negotiation-balance/topups              org_payments create (Idempotency-Key)
 *   GET  /payment/negotiation-balance/topups/:id          org_payments view | bid view
 *   POST /payment/negotiation-balance/topups/:id/cancel   org_payments delete | the requester
 *   PUT  /payment/negotiation-balance/auto-topup          org_payments edit
 *
 * The UI is server-declared: nothing renders unless GET answers 200 with
 * `enabled` and an account. While the Payments flag is off that GET answers
 * 404 `{"error":"negotiation_balance_disabled"}`, which this client reads as
 * "no balance" (`null`), never as an outage.
 *
 * Payments decodes bodies strictly, so the request shapes here are exact. Its
 * errors are `{"error": code}`; only the allowlisted codes below are read from
 * a body, and nothing else in it is displayed. No function here throws: each
 * returns a result the caller must branch on. An optional answer field that is
 * null reads as absent; `account` is the one field where null has a meaning
 * (no account).
 */

export { newIdempotencyKey };
export const NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT =
  "dakasa:negotiation-balance-readiness-changed";

export type NegotiationRail = "efi" | "prepaid" | "unknown";
export type NegotiationAccountState = "active" | "restricted" | "closed" | "unknown";
export type NegotiationTopupState =
  | "requested"
  | "put_unknown"
  | "active"
  | "receipt_pending"
  | "confirmed"
  | "expired"
  | "removed"
  | "abandoned"
  | "unknown";
export type NegotiationTopupChannel = "cob" | "weekly_line" | "unknown";
export type NegotiationTopupOrigin = "payments_tab" | "auction";
export type NegotiationEntryKind =
  | "topup_credit"
  | "consumption_debit"
  | "refund_debit"
  | "refund_failed_credit"
  | "reversal_debit"
  | "unknown";
export type AutoTopupMode = "off" | "weekly_fixed" | "unknown";
export type AutoTopupIneligibleReason =
  | "pix_automatico_not_approved"
  | "weekly_limit_missing"
  | "bank_limit_suspected"
  // Not in C4 yet: read ahead of Payments refusing an automatic top-up
  // without the accepted conditions (owner decision 11).
  | "terms_required"
  | "unknown";

const RAILS = new Set(["efi", "prepaid"]);
const ACCOUNT_STATES = new Set(["active", "restricted", "closed"]);
const TOPUP_STATES = new Set([
  "requested",
  "put_unknown",
  "active",
  "receipt_pending",
  "confirmed",
  "expired",
  "removed",
  "abandoned",
]);
const TOPUP_CHANNELS = new Set(["cob", "weekly_line"]);
const ENTRY_KINDS = new Set([
  "topup_credit",
  "consumption_debit",
  "refund_debit",
  "refund_failed_credit",
  "reversal_debit",
]);
const AUTO_TOPUP_MODES = new Set(["off", "weekly_fixed"]);
const INELIGIBLE_REASONS = new Set([
  "pix_automatico_not_approved",
  "weekly_limit_missing",
  "bank_limit_suspected",
  "terms_required",
]);

export type NegotiationAccount = {
  state: NegotiationAccountState;
  restrictedReason?: string;
};

/**
 * A top-up still in transit, shown as "Em confirmação" and never counted.
 * `netCents` is what it credits, exactly, once confirmed; `chargeCents` what
 * its Pix charges; `feeCents` the top-up fee between them (0 on a weekly
 * line, whose Pix fee is the weekly fee line).
 */
export type NegotiationPendingTopup = {
  id: string;
  channel: NegotiationTopupChannel;
  state: NegotiationTopupState;
  netCents: number;
  feeCents: number;
  chargeCents: number;
  expiresAt?: string;
};

export type NegotiationBalanceTerms = {
  /** The version Payments requires (NEGOTIATION_BALANCE_TERMS_VERSION). */
  version: string;
  acceptedVersion?: string;
  acceptedAt?: string;
  /** True while the company has not accepted `version`. */
  required: boolean;
};

export type NegotiationTopupLimits = {
  minTopupCents: number;
  maxTopupCents: number;
  maxOpenTopups: number;
};

/**
 * The top-up fee of a recharge QR (owner decision 2026-10-30, gross-up): for
 * the net the company wants in its balance the fee is max(ceil(net × bps /
 * 10000), floorCents) and the QR charges net + fee. The balance is credited
 * with exactly the net, and the fee is never refunded. Production declares
 * bps 200 and floorCents 20 (2 %, at least R$ 0,20); the UI prices with what
 * Payments declares, never with those numbers.
 */
export type NegotiationTopupFee = { bps: number; floorCents: number };

export type NegotiationAutoTopup = {
  /** Payments offers the weekly automatic top-up at all (its flag). */
  available: boolean;
  mode: AutoTopupMode;
  amountCents?: number;
  version: number;
  eligible: boolean;
  ineligibleReason?: AutoTopupIneligibleReason;
};

export type NegotiationBalance = {
  rail: NegotiationRail;
  /** The prepaid rail starts admitting bids at this instant (RFC 3339). */
  railFrom?: string;
  account: NegotiationAccount | null;
  /** Signed: a returned Pix can leave it below zero. */
  balanceCents: number;
  reservedCents: number;
  availableCents: number;
  /**
   * The sum of the nets of the open cob (QR) top-ups, never counted. A weekly
   * line in `pendingTopups` (planned on a sealed weekly charge) is not in it.
   */
  pendingTopupCents: number;
  pendingTopups: NegotiationPendingTopup[];
  terms: NegotiationBalanceTerms;
  topupsEnabled: boolean;
  /** Limits of the net a recharge asks for. */
  limits: NegotiationTopupLimits;
  /** Present while Payments has a top-up fee configured; no QR is offered without it. */
  topupFee?: NegotiationTopupFee;
  autoTopup: NegotiationAutoTopup;
  revision: number;
};

/**
 * One recharge QR. The company never sees the actual Efí fee: it is DaKasa's
 * cost and never moves the balance.
 */
export type NegotiationTopup = {
  id: string;
  channel: NegotiationTopupChannel;
  origin: NegotiationTopupOrigin | "unknown";
  state: NegotiationTopupState;
  /** The balance the company chose: credited exactly once the receipt is confirmed. */
  netCents: number;
  /** The top-up fee charged on top of the net; never refunded. */
  feeCents: number;
  /** netCents + feeCents: the amount of the QR. */
  chargeCents: number;
  expiresAt: string;
  pixCopiaECola?: string;
  /** Present only once confirmed; Payments credits exactly netCents. */
  creditedCents?: number;
  confirmedAt?: string;
  createdAt: string;
};

export type NegotiationBalanceEntry = {
  id: string;
  kind: NegotiationEntryKind;
  /** Signed: credits are positive, debits negative. */
  amountCents: number;
  createdAt: string;
  topupId?: string;
  invoiceId?: string;
  cycleId?: string;
  refundId?: string;
  reason?: string;
};

export type NegotiationBalanceEntriesPage = {
  entries: NegotiationBalanceEntry[];
  nextCursor?: string;
};

export type NegotiationTermsAcceptance = { termsVersion: string; acceptedAt: string };

/**
 * The `negotiationBalance` field of GET /negotiate/org-tax-preview (Ads). It is
 * present only for a company on the prepaid rail, and the bid is admitted by
 * Payments, never by this preview.
 */
export type NegotiationBalancePreview = {
  rail: "prepaid";
  accountState: "active" | "restricted";
  prepaidRailEnabled: boolean;
  availableCents: number;
  reservedCents: number;
  /** Held by the company's previous (autobuy) bid on this same post. */
  reservedOnPostCents: number;
  /** The sum of the nets of the open cob (QR) top-ups, as on the balance. */
  pendingTopupCents: number;
  requiredCents: number;
  missingCents: number;
};

/** Payments codes read from a body, plus the transport classes seen before its handler. */
export type NegotiationBalanceErrorCode =
  | "negotiation_balance_disabled"
  | "negotiation_terms_required"
  | "negotiation_terms_version_mismatch"
  | "negotiation_topup_limit"
  | "negotiation_account_missing"
  | "negotiation_account_closed"
  | "negotiation_fiscal_profile_missing"
  | "invalid_topup_amount"
  | "negotiation_topups_disabled"
  | "negotiation_auto_topup_stale"
  | "negotiation_auto_topup_ineligible"
  | "negotiation_auto_topup_disabled"
  | "unauthorized"
  | "forbidden"
  | "route_missing"
  | "rate_limited"
  | "network"
  | "unavailable"
  | "malformed_response"
  | "unexpected";

const BACKEND_CODES: ReadonlySet<string> = new Set<NegotiationBalanceErrorCode>([
  "negotiation_balance_disabled",
  "negotiation_terms_required",
  "negotiation_terms_version_mismatch",
  "negotiation_topup_limit",
  "negotiation_account_missing",
  "negotiation_account_closed",
  "negotiation_fiscal_profile_missing",
  "invalid_topup_amount",
  "negotiation_topups_disabled",
  "negotiation_auto_topup_stale",
  "negotiation_auto_topup_ineligible",
  "negotiation_auto_topup_disabled",
]);

export type NegotiationBalanceFailure = {
  code: NegotiationBalanceErrorCode;
  status?: number;
  /** Only for negotiation_auto_topup_ineligible: why the automatic top-up is not allowed. */
  reason?: AutoTopupIneligibleReason;
};

export type NegotiationBalanceResult<T> =
  | { ok: true; value: T }
  | { ok: false; failure: NegotiationBalanceFailure };

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function text(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && value.length <= 512;
}

/**
 * An optional field Payments did not fill. C4 marks optional fields with "?"
 * only; a Go pointer without omitempty sends null instead of omitting the key,
 * so both read as absent. A present value of the wrong shape still refuses the
 * whole answer.
 */
function absent(value: unknown): value is undefined | null {
  return value === undefined || value === null;
}

function optionalText(value: unknown): string | undefined | null {
  if (absent(value)) return undefined;
  return text(value) ? value : null;
}

function timestamp(value: unknown): value is string {
  return text(value) && /^\d{4}-\d{2}-\d{2}T/.test(value) && Number.isFinite(Date.parse(value));
}

function optionalTimestamp(value: unknown): string | undefined | null {
  if (absent(value)) return undefined;
  return timestamp(value) ? value : null;
}

function cents(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function signedCents(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value);
}

function optionalCents(value: unknown): number | undefined | null {
  if (absent(value)) return undefined;
  return cents(value) ? value : null;
}

function known<T extends string>(values: ReadonlySet<string>, value: string): T | "unknown" {
  return (values.has(value) ? value : "unknown") as T | "unknown";
}

/**
 * Classifies a rejected request. Only the allowlisted Payments codes are read
 * from the body. A missing permission reaches the browser as Payments' RBAC
 * 500 `{"error": true, ...}`, which reads as `unavailable`.
 */
export function toNegotiationBalanceFailure(error: unknown): NegotiationBalanceFailure {
  const status = getErrorStatus(error);
  const body = record(record(record(error)?.response)?.data);
  const code = body?.error;
  if (typeof code === "string" && BACKEND_CODES.has(code)) {
    const failure: NegotiationBalanceFailure = {
      code: code as NegotiationBalanceErrorCode,
      status,
    };
    if (code === "negotiation_auto_topup_ineligible" && typeof body?.reason === "string") {
      failure.reason = known<AutoTopupIneligibleReason>(INELIGIBLE_REASONS, body.reason);
    }
    return failure;
  }
  if (status === undefined || status === 0) return { code: "network", status };
  if (status === 401) return { code: "unauthorized", status };
  if (status === 403) return { code: "forbidden", status };
  // A 404 without the Payments envelope is a build without these routes (or a
  // wrong base URL); a top-up of another organization answers the same way.
  if (status === 404) return { code: "route_missing", status };
  if (status === 429) return { code: "rate_limited", status };
  if (status >= 500) return { code: "unavailable", status };
  return { code: "unexpected", status };
}

/** Transient failures a caller may retry with the same request (and the same key). */
export function isTransientNegotiationFailure(failure: NegotiationBalanceFailure): boolean {
  return ["network", "unavailable", "rate_limited", "malformed_response"].includes(failure.code);
}

function parseAccount(value: unknown): NegotiationAccount | null | undefined {
  if (value === null) return null;
  const body = record(value);
  if (!body || typeof body.state !== "string") return undefined;
  const restrictedReason = optionalText(body.restrictedReason);
  if (restrictedReason === null) return undefined;
  return {
    state: known<NegotiationAccountState>(ACCOUNT_STATES, body.state),
    ...(restrictedReason === undefined ? {} : { restrictedReason }),
  };
}

type TopupAmounts = { netCents: number; feeCents: number; chargeCents: number };

/**
 * The three amounts of a top-up: its Pix charges exactly the net plus the
 * fee. An answer where they disagree is refused, so no screen shows a fee or
 * a total the QR does not carry.
 */
function topupAmounts(body: Record<string, unknown>): TopupAmounts | null {
  const { netCents, feeCents, chargeCents } = body;
  if (
    !cents(netCents) ||
    !cents(feeCents) ||
    !cents(chargeCents) ||
    netCents <= 0 ||
    chargeCents !== netCents + feeCents
  )
    return null;
  return { netCents, feeCents, chargeCents };
}

function parsePendingTopup(value: unknown): NegotiationPendingTopup | null {
  const body = record(value);
  if (!body || !text(body.id) || typeof body.channel !== "string" || typeof body.state !== "string")
    return null;
  const amounts = topupAmounts(body);
  const expiresAt = optionalTimestamp(body.expiresAt);
  if (amounts === null || expiresAt === null) return null;
  return {
    id: body.id,
    channel: known<NegotiationTopupChannel>(TOPUP_CHANNELS, body.channel),
    state: known<NegotiationTopupState>(TOPUP_STATES, body.state),
    ...amounts,
    ...(expiresAt === undefined ? {} : { expiresAt }),
  };
}

function parseTerms(value: unknown): NegotiationBalanceTerms | null {
  const body = record(value);
  if (!body || typeof body.version !== "string" || typeof body.required !== "boolean") return null;
  const acceptedVersion = optionalText(body.acceptedVersion);
  const acceptedAt = optionalTimestamp(body.acceptedAt);
  if (acceptedVersion === null || acceptedAt === null) return null;
  return {
    version: body.version,
    required: body.required,
    ...(acceptedVersion === undefined ? {} : { acceptedVersion }),
    ...(acceptedAt === undefined ? {} : { acceptedAt }),
  };
}

function parseLimits(value: unknown): NegotiationTopupLimits | null {
  const body = record(value);
  if (
    !body ||
    !cents(body.minTopupCents) ||
    !cents(body.maxTopupCents) ||
    !cents(body.maxOpenTopups) ||
    body.minTopupCents <= 0 ||
    body.maxTopupCents < body.minTopupCents
  )
    return null;
  return {
    minTopupCents: body.minTopupCents,
    maxTopupCents: body.maxTopupCents,
    maxOpenTopups: body.maxOpenTopups,
  };
}

function parseTopupFee(value: unknown): NegotiationTopupFee | undefined | null {
  if (absent(value)) return undefined;
  const body = record(value);
  if (!body || !cents(body.bps) || !cents(body.floorCents) || body.bps > 10000) return null;
  return { bps: body.bps, floorCents: body.floorCents };
}

export function parseAutoTopup(value: unknown): NegotiationAutoTopup | null {
  const body = record(value);
  if (
    !body ||
    typeof body.available !== "boolean" ||
    typeof body.mode !== "string" ||
    !cents(body.version) ||
    typeof body.eligible !== "boolean"
  )
    return null;
  const amountCents = optionalCents(body.amountCents);
  if (amountCents === null) return null;
  if (!absent(body.ineligibleReason) && typeof body.ineligibleReason !== "string") return null;
  return {
    available: body.available,
    mode: known<AutoTopupMode>(AUTO_TOPUP_MODES, body.mode),
    ...(amountCents === undefined ? {} : { amountCents }),
    version: body.version,
    eligible: body.eligible,
    ...(typeof body.ineligibleReason === "string"
      ? {
          ineligibleReason: known<AutoTopupIneligibleReason>(
            INELIGIBLE_REASONS,
            body.ineligibleReason,
          ),
        }
      : {}),
  };
}

/**
 * Reads GET /payment/negotiation-balance. `null` means the answer does not
 * declare an enabled balance; a malformed answer is refused as a whole so a
 * partial body is never shown as an amount.
 */
export function parseNegotiationBalance(data: unknown): NegotiationBalance | null {
  const body = record(data);
  if (!body || body.enabled !== true || typeof body.rail !== "string") return null;
  const account = parseAccount(body.account);
  const railFrom = optionalTimestamp(body.railFrom);
  const terms = parseTerms(body.terms);
  const limits = parseLimits(body.limits);
  const topupFee = parseTopupFee(body.topupFee);
  const autoTopup = parseAutoTopup(body.autoTopup);
  if (
    account === undefined ||
    railFrom === null ||
    terms === null ||
    limits === null ||
    topupFee === null ||
    autoTopup === null ||
    !signedCents(body.balanceCents) ||
    !cents(body.reservedCents) ||
    !cents(body.availableCents) ||
    !cents(body.pendingTopupCents) ||
    typeof body.topupsEnabled !== "boolean" ||
    !cents(body.revision) ||
    !Array.isArray(body.pendingTopups) ||
    body.pendingTopups.length > 100
  )
    return null;
  const pendingTopups: NegotiationPendingTopup[] = [];
  for (const candidate of body.pendingTopups) {
    const parsed = parsePendingTopup(candidate);
    if (!parsed) return null;
    pendingTopups.push(parsed);
  }
  return {
    rail: known<NegotiationRail>(RAILS, body.rail),
    ...(railFrom === undefined ? {} : { railFrom }),
    account,
    balanceCents: body.balanceCents,
    reservedCents: body.reservedCents,
    availableCents: body.availableCents,
    pendingTopupCents: body.pendingTopupCents,
    pendingTopups,
    terms,
    topupsEnabled: body.topupsEnabled,
    limits,
    ...(topupFee === undefined ? {} : { topupFee }),
    autoTopup,
    revision: body.revision,
  };
}

export function parseNegotiationTopup(data: unknown): NegotiationTopup | null {
  const body = record(data);
  if (
    !body ||
    !text(body.id) ||
    typeof body.channel !== "string" ||
    typeof body.origin !== "string" ||
    typeof body.state !== "string" ||
    !timestamp(body.expiresAt) ||
    !timestamp(body.createdAt)
  )
    return null;
  const amounts = topupAmounts(body);
  const pixCopiaECola = optionalText(body.pixCopiaECola);
  const creditedCents = optionalCents(body.creditedCents);
  const confirmedAt = optionalTimestamp(body.confirmedAt);
  if (amounts === null || pixCopiaECola === null || creditedCents === null || confirmedAt === null)
    return null;
  return {
    id: body.id,
    channel: known<NegotiationTopupChannel>(TOPUP_CHANNELS, body.channel),
    origin: body.origin === "payments_tab" || body.origin === "auction" ? body.origin : "unknown",
    state: known<NegotiationTopupState>(TOPUP_STATES, body.state),
    ...amounts,
    expiresAt: body.expiresAt,
    createdAt: body.createdAt,
    ...(pixCopiaECola === undefined ? {} : { pixCopiaECola }),
    ...(creditedCents === undefined ? {} : { creditedCents }),
    ...(confirmedAt === undefined ? {} : { confirmedAt }),
  };
}

function parseEntry(value: unknown): NegotiationBalanceEntry | null {
  const body = record(value);
  if (
    !body ||
    !text(body.id) ||
    typeof body.kind !== "string" ||
    !signedCents(body.amountCents) ||
    !timestamp(body.createdAt)
  )
    return null;
  const optional: Partial<NegotiationBalanceEntry> = {};
  for (const field of ["topupId", "invoiceId", "cycleId", "refundId", "reason"] as const) {
    const parsed = optionalText(body[field]);
    if (parsed === null) return null;
    if (parsed !== undefined) optional[field] = parsed;
  }
  return {
    id: body.id,
    kind: known<NegotiationEntryKind>(ENTRY_KINDS, body.kind),
    amountCents: body.amountCents,
    createdAt: body.createdAt,
    ...optional,
  };
}

export function parseNegotiationBalanceEntries(
  data: unknown,
): NegotiationBalanceEntriesPage | null {
  const body = record(data);
  if (!body || !Array.isArray(body.entries) || body.entries.length > 100) return null;
  const nextCursor = optionalText(body.nextCursor);
  if (nextCursor === null) return null;
  const entries: NegotiationBalanceEntry[] = [];
  for (const candidate of body.entries) {
    const parsed = parseEntry(candidate);
    if (!parsed) return null;
    entries.push(parsed);
  }
  return { entries, ...(nextCursor === undefined ? {} : { nextCursor }) };
}

function parseTermsAcceptance(data: unknown): NegotiationTermsAcceptance | null {
  const body = record(data);
  if (!body || !text(body.termsVersion) || !timestamp(body.acceptedAt)) return null;
  return { termsVersion: body.termsVersion, acceptedAt: body.acceptedAt };
}

/**
 * Reads the optional `negotiationBalance` of an org-tax-preview answer.
 * `undefined` means the company is not on the prepaid rail (or the field is
 * malformed): the preventive UX then stays out of the way and the server
 * still decides the bid.
 */
export function parseNegotiationBalancePreview(
  value: unknown,
): NegotiationBalancePreview | undefined {
  const body = record(value);
  if (
    !body ||
    body.rail !== "prepaid" ||
    (body.accountState !== "active" && body.accountState !== "restricted") ||
    typeof body.prepaidRailEnabled !== "boolean" ||
    !cents(body.availableCents) ||
    !cents(body.reservedCents) ||
    !cents(body.reservedOnPostCents) ||
    !cents(body.pendingTopupCents) ||
    !cents(body.requiredCents) ||
    !cents(body.missingCents)
  )
    return undefined;
  return {
    rail: "prepaid",
    accountState: body.accountState,
    prepaidRailEnabled: body.prepaidRailEnabled,
    availableCents: body.availableCents,
    reservedCents: body.reservedCents,
    reservedOnPostCents: body.reservedOnPostCents,
    pendingTopupCents: body.pendingTopupCents,
    requiredCents: body.requiredCents,
    missingCents: body.missingCents,
  };
}

const MALFORMED: NegotiationBalanceFailure = { code: "malformed_response" };

async function request<T>(
  call: () => Promise<{ data: unknown }>,
  parse: (data: unknown) => T | null,
): Promise<NegotiationBalanceResult<T>> {
  let data: unknown;
  try {
    data = (await call()).data;
  } catch (error) {
    return { ok: false, failure: toNegotiationBalanceFailure(error) };
  }
  const value = parse(data);
  return value === null ? { ok: false, failure: MALFORMED } : { ok: true, value };
}

const headers = (jwt: string, idempotencyKey?: string) => ({
  headers: idempotencyKey ? { "X-JWT": jwt, "Idempotency-Key": idempotencyKey } : { "X-JWT": jwt },
});

/** `null` means Payments does not offer the balance (its flag is off): render nothing. */
export async function getNegotiationBalance(
  jwt: string,
): Promise<NegotiationBalanceResult<NegotiationBalance | null>> {
  const result = await request(
    () => paymentAPI.get("/negotiation-balance", headers(jwt)),
    (data) => {
      const body = record(data);
      // A 200 that says enabled false is the same as the flag being off.
      if (body?.enabled === false) return { disabled: true as const };
      return parseNegotiationBalance(data);
    },
  );
  if (!result.ok) {
    return result.failure.code === "negotiation_balance_disabled"
      ? { ok: true, value: null }
      : result;
  }
  return { ok: true, value: "disabled" in result.value ? null : result.value };
}

export type AcceptNegotiationTermsInput = { idempotencyKey: string; termsVersion: string };

export function acceptNegotiationTerms(
  jwt: string,
  input: AcceptNegotiationTermsInput,
): Promise<NegotiationBalanceResult<NegotiationTermsAcceptance>> {
  const body = { termsVersion: input.termsVersion };
  return request(
    () =>
      paymentAPI.post(
        "/negotiation-balance/terms-acceptance",
        body,
        headers(jwt, input.idempotencyKey),
      ),
    parseTermsAcceptance,
  );
}

export type CreateTopupInput = {
  /**
   * One key per attempt: a retry of the same attempt (same net and origin)
   * reuses it, so an uncertain first answer can never create a second QR.
   */
  idempotencyKey: string;
  /**
   * The balance the company wants, a whole BRL amount within `limits`. The QR
   * Payments creates charges it plus the top-up fee.
   */
  netCents: number;
  origin: NegotiationTopupOrigin;
};

/** 201 or 202 (requested | put_unknown) both carry a top-up; its state says which. */
export function createTopup(
  jwt: string,
  input: CreateTopupInput,
): Promise<NegotiationBalanceResult<NegotiationTopup>> {
  const body = { netCents: input.netCents, origin: input.origin };
  return request(
    () => paymentAPI.post("/negotiation-balance/topups", body, headers(jwt, input.idempotencyKey)),
    parseNegotiationTopup,
  );
}

export function getTopup(
  jwt: string,
  id: string,
): Promise<NegotiationBalanceResult<NegotiationTopup>> {
  return request(
    () => paymentAPI.get(`/negotiation-balance/topups/${encodeURIComponent(id)}`, headers(jwt)),
    parseNegotiationTopup,
  );
}

/** 202 with the top-up; its state changes only once Efí shows no payment on the QR. */
export function cancelTopup(
  jwt: string,
  id: string,
): Promise<NegotiationBalanceResult<NegotiationTopup>> {
  return request(
    () =>
      paymentAPI.post(
        `/negotiation-balance/topups/${encodeURIComponent(id)}/cancel`,
        {},
        headers(jwt),
      ),
    parseNegotiationTopup,
  );
}

export type ListEntriesInput = { cursor?: string; limit?: number };

export function listEntries(
  jwt: string,
  input: ListEntriesInput = {},
): Promise<NegotiationBalanceResult<NegotiationBalanceEntriesPage>> {
  const limit = Math.min(100, Math.max(1, Math.trunc(input.limit ?? 20)));
  const params: Record<string, string> = { limit: String(limit) };
  if (input.cursor) params.cursor = input.cursor;
  return request(
    () => paymentAPI.get("/negotiation-balance/entries", { ...headers(jwt), params }),
    parseNegotiationBalanceEntries,
  );
}

export type PutAutoTopupInput =
  | { mode: "off"; version: number }
  | { mode: "weekly_fixed"; amountCents: number; version: number; bankLimitAcknowledged: true };

export function putAutoTopup(
  jwt: string,
  input: PutAutoTopupInput,
): Promise<NegotiationBalanceResult<NegotiationAutoTopup>> {
  const body =
    input.mode === "off"
      ? { mode: "off", version: input.version }
      : {
          mode: "weekly_fixed",
          amountCents: input.amountCents,
          version: input.version,
          bankLimitAcknowledged: true,
        };
  return request(
    () => paymentAPI.put("/negotiation-balance/auto-topup", body, headers(jwt)),
    parseAutoTopup,
  );
}
